/**
 * Asks a handheld device held in portrait to rotate, and suspends play until it does.
 *
 * Gated on a *coarse pointer* as well as portrait orientation: a desktop browser in a tall
 * narrow window is not a phone held sideways, and telling someone to rotate their monitor
 * would be absurd. iOS has no orientation lock available to web apps, so asking is the
 * only option open to us.
 */

import { useEffect, useRef, useState } from 'react';
import { useGameStore } from '../store/gameStore';

function matches(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

function subscribe(query: string, onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }
  const list = window.matchMedia(query);
  list.addEventListener('change', onChange);
  return () => list.removeEventListener('change', onChange);
}

const PORTRAIT = '(orientation: portrait)';
const COARSE_POINTER = '(pointer: coarse)';

export function OrientationGate() {
  const dispatch = useGameStore((store) => store.dispatch);
  const [blocked, setBlocked] = useState(() => matches(PORTRAIT) && matches(COARSE_POINTER));

  useEffect(() => {
    const update = () => setBlocked(matches(PORTRAIT) && matches(COARSE_POINTER));

    const unsubscribePortrait = subscribe(PORTRAIT, update);
    const unsubscribePointer = subscribe(COARSE_POINTER, update);
    update();

    return () => {
      unsubscribePortrait();
      unsubscribePointer();
    };
  }, []);

  // Only resume a pause this component caused. Without the guard, mounting in landscape
  // would dispatch RESUME and silently undo a pause the player asked for.
  const pausedByUs = useRef(false);

  useEffect(() => {
    // Suspending input is what actually matters — the sticks are unreachable and the view
    // is unusable in portrait, so play must not continue underneath the overlay.
    if (blocked) {
      pausedByUs.current = true;
      dispatch('PAUSE');
    } else if (pausedByUs.current) {
      pausedByUs.current = false;
      dispatch('RESUME');
    }
  }, [blocked, dispatch]);

  if (!blocked) return null;

  return (
    <div className="overlay">
      <div role="status" aria-live="polite">
        <h2>Rotate your device</h2>
        <p>Lab17 Labyrint is played in landscape. Turn your device sideways to continue.</p>
      </div>
    </div>
  );
}
